import { FileInput } from "@/components/file-input";
import { Section } from "@/components/section";
import { IS_PRO, IS_PUBLIC_SITE } from "@/utils/constants";
import { css } from "@/utils/css";
import { __ } from "@wordpress/i18n";
import { pdf, Document, Page, Text } from "@react-pdf/renderer";
import { useProModal } from "@/components/pro-alert-box";

const acceptedFiles = [
    {
        value: "txt",
        label: __("Text", "antimanual"),
    },
    {
        value: "html",
        label: __("HTML", "antimanual"),
    },
    {
        value: "md",
        label: __("Markdown", "antimanual"),
    },
    {
        value: "csv",
        label: __("CSV", "antimanual"),
    },
    {
        value: "json",
        label: __("JSON", "antimanual"),
    },
    {
        value: "pdf",
        label: __("PDF", "antimanual"),
    },
];

type Props = React.HTMLProps<HTMLDivElement> & {
    handleFileChange: (files: File[] | undefined) => void;
};

export function KnowledgeSources({ handleFileChange, className }: Props) {
    const { showProAlert } = useProModal();

    const _handleFileChange = async (files: File[] | undefined) => {
        if (!files?.length) return;

        const newFiles: File[] = [];

        for (const file of files) {
            if (file.type === "application/pdf") {
                newFiles.push(file);
                continue;
            }

            const text = await file.text();

            const blob = await pdf(
                <Document>
                    <Page style={{ padding: 20, fontFamily: "Courier", fontSize: 10 }}>
                        <Text>{text}</Text>
                    </Page>
                </Document>
            ).toBlob();

            newFiles.push(new File([blob], file.name + ".pdf", { type: "application/pdf" }));
        }

        handleFileChange(newFiles);
    };

    return (
        <Section className={css("bg-light", className)}>
            <h3>{__("Knowledge Sources", "antimanual")}</h3>
            <FileInput
                multiple
                showFiles
                accept={acceptedFiles.map((file) => `.${file.value}`).join(",")}
                label={__("Upload knowledge files", "antimanual")}
                help={
                    <>
                        <span>{__("AI will use the information in these files to generate posts.", "antimanual")}</span>
                        <br />
                        <span>{__("You can upload", "antimanual")}</span>{" "}
                        {acceptedFiles.map((file, index) => {
                            const isLast = index === acceptedFiles.length - 1;
                            const isFirst = index === 0;

                            return (
                                <span key={file.value}>
                                    {isFirst ? "" : isLast ? `, ${__("or", "antimanual")} ` : ", "}
                                    {file.label}
                                </span>
                            );
                        })}{" "}
                        {__("files.", "antimanual")}
                        {!IS_PUBLIC_SITE && (
                            <span className={css("text-olive font-semibold block mt-1")}>
                                {__("Note: Your website must be publicly accessible for the AI to read files.", "antimanual")}
                            </span>
                        )}
                    </>
                }
                className={css("max-w-sm")}
                handleFileChange={_handleFileChange}
                handleClick={(e) => {
                    if (!IS_PRO) {
                        e.preventDefault();

                        showProAlert(
                            __("Premium Feature", "antimanual"),
                            __("Knowledge files upload is a premium feature. Upgrade to upload knowledge files.", "antimanual")
                        );
                    }
                }}
            />
        </Section>
    );
}
